import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Rate,
  RateFormValues,
  RateGroups,
  RateGroupsFormValues,
} from './rate.types'
import fetcher from '@/core/lib/fetcher'
import { APIResponse } from '@/core/types/utils'
import { useParams } from '@tanstack/react-router'

export const ratesQueryOptions = (siteId: string, token: string) =>
  queryOptions({
    queryKey: ['rates', siteId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `property/${siteId}/rates`)
        .json<Promise<APIResponse<Rate[]>>>(),
  })

export const rateByIdQueryOptions = (id: string, token: string) => {
  return queryOptions({
    queryKey: ['rate', id, token],
    queryFn: id
      ? () =>
          fetcher
            .auth(`Bearer ${token}`)
            .get(import.meta.env.VITE_API_BOOKING + `property/rate/${id}`)
            .json<Promise<APIResponse<Rate>>>()
      : undefined,
    enabled: id !== '',
  })
}

export const createRate = (
  data: RateFormValues,
  siteId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}/rates`)
    .post(data)
    .json()

export const updateRate = (
  data: RateFormValues,
  rateId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/rate/${rateId}`)
    .put(data)
    .json()

export const useDeleteRate = (token: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(import.meta.env.VITE_API_BOOKING + `property/rate/${id}`)
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['rates'],
      })
    },
  })
}
export const validateRateCode = (
  propertyId: string,
  rateCode: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(
      import.meta.env.VITE_API_BOOKING +
        `property/${propertyId}/rates/check-code/${encodeURIComponent(rateCode)}`
    )
    .get()
    .json()
export const getRateGroups = (siteId: string, token: string) =>
  queryOptions({
    queryKey: ['rateGroups', siteId, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .get(import.meta.env.VITE_API_BOOKING + `property/${siteId}/rate-group`)
        .json<Promise<APIResponse<RateGroups[]>>>(),
  })

export const getRateGroupById = (id: string, siteId: string, token: string) => {
  return queryOptions({
    queryKey: ['rateGroup', id, siteId, token],
    queryFn: id
      ? () =>
          fetcher
            .auth(`Bearer ${token}`)
            .get(
              import.meta.env.VITE_API_BOOKING +
                `property/${siteId}/rate-group/${id}`
            )
            .json<Promise<APIResponse<RateGroups>>>()
      : undefined,
    enabled: id !== '',
  })
}

export const createRateGroup = (
  data: RateGroupsFormValues,
  siteId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(import.meta.env.VITE_API_BOOKING + `property/${siteId}/rate-group`)
    .post(data)
    .json()

export const updateRateGroup = (
  data: RateGroupsFormValues,
  rateId: string,
  siteId: string,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(
      import.meta.env.VITE_API_BOOKING +
        `property/${siteId}/rate-group/${rateId}`
    )
    .put(data)
    .json()

export const useDeleteRateGroup = (token: string) => {
  const { siteId } = useParams({
    from: '/_layout/dashboard/sites/$siteId/rates-and-packages/rates/',
  })
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(
          import.meta.env.VITE_API_BOOKING +
            `property/${siteId}/rate-group/${id}`
        )
        .delete()
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['rateGroups'],
      })
    },
  })
}
